import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'lib-tooltip',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tooltip.component.html',
  styleUrl: './tooltip.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class TooltipComponent implements OnDestroy {
  @Input() anchor: HTMLElement | null = null;

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private visible = false;
  private animationFrameId?: number;
  private resizeObserver?: ResizeObserver;

  @HostBinding('class.tooltip-anchor-positioned')
  get hasAnchor(): boolean {
    return !!this.anchor;
  }

  get isVisible(): boolean {
    return this.visible;
  }

  set isVisible(value: boolean) {
    if (this.visible === value) {
      return;
    }

    this.visible = value;

    if (value) {
      this.schedulePositionUpdate();
      this.observePositionChanges();
    } else {
      this.stopObserving();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isVisible) {
      this.schedulePositionUpdate();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.isVisible) {
      this.schedulePositionUpdate();
    }
  }

  @HostListener('document:click', ['$event'])
  clickOut(event: MouseEvent): void {
    const target = event.target as Node | null;
    const host = this.elementRef.nativeElement;
    const parent = host.parentElement;

    if (!target) {
      return;
    }

    if (
      host.contains(target) ||
      this.anchor?.contains(target) ||
      parent?.contains(target)
    ) {
      return;
    }

    this.isVisible = false;
  }

  private schedulePositionUpdate(): void {
    const windowRef = this.elementRef.nativeElement.ownerDocument.defaultView;

    if (!windowRef) {
      return;
    }

    if (this.animationFrameId !== undefined) {
      windowRef.cancelAnimationFrame(this.animationFrameId);
    }

    this.animationFrameId = windowRef.requestAnimationFrame(() => {
      this.updatePosition();
      this.animationFrameId = undefined;
    });
  }

  private updatePosition(): void {
    if (!this.anchor || !this.isVisible) {
      return;
    }

    const host = this.elementRef.nativeElement;
    const offsetParent = host.offsetParent as HTMLElement | null;

    if (!offsetParent) {
      return;
    }

    const tooltipRect = host.getBoundingClientRect();
    const anchorRect = this.anchor.getBoundingClientRect();
    const parentRect = offsetParent.getBoundingClientRect();

    if (!tooltipRect.width) {
      return;
    }

    const windowRef = host.ownerDocument.defaultView;

    if (!windowRef) {
      return;
    }

    const viewportPadding = 12;
    const arrowPadding = 12;

    const anchorCenter = anchorRect.left + anchorRect.width / 2;

    const desiredViewportLeft = anchorCenter - tooltipRect.width / 2;

    const maximumViewportLeft = Math.max(
      viewportPadding,
      windowRef.innerWidth - tooltipRect.width - viewportPadding
    );

    const viewportLeft = Math.min(
      Math.max(desiredViewportLeft, viewportPadding),
      maximumViewportLeft
    );

    const tooltipLeft =
      viewportLeft - parentRect.left + offsetParent.scrollLeft;

    const arrowLeft = Math.min(
      Math.max(anchorCenter - viewportLeft, arrowPadding),
      tooltipRect.width - arrowPadding
    );

    host.style.insetInlineStart = 'auto';
    host.style.insetInlineEnd = 'auto';
    host.style.right = 'auto';
    host.style.left = `${tooltipLeft}px`;

    host.style.setProperty('--tooltip-arrow-left', `${arrowLeft}px`);
  }

  private observePositionChanges(): void {
    this.stopObserving();

    if (!this.anchor || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.schedulePositionUpdate();
    });

    this.resizeObserver.observe(this.anchor);

    const parent = this.elementRef.nativeElement.parentElement;

    if (parent) {
      this.resizeObserver.observe(parent);
    }
  }

  private stopObserving(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
  }

  ngOnDestroy(): void {
    const windowRef = this.elementRef.nativeElement.ownerDocument.defaultView;

    if (windowRef && this.animationFrameId !== undefined) {
      windowRef.cancelAnimationFrame(this.animationFrameId);
    }

    this.stopObserving();
  }
}
